import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { 
  FaTimes, FaCopy, FaExternalLinkAlt, FaPlay, FaApple, FaAndroid, FaGlobe, FaAd
} from 'react-icons/fa';
import { getBaseUrl } from '../utils/tokenUtils';

const InternalAdsHowToModal = ({ isOpen, onClose, placement, onCopyApiKey }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && placement?.projectId?._id) {
      fetchProducts();
    }
  }, [isOpen, placement]);

  const fetchProducts = async () => {
    if (!placement?.projectId?._id) return;
    
    setLoading(true);
    try {
      // Fetch products only for the placement's own project
      const response = await fetch(`/api/dashboard/user/projects/${placement.projectId._id}/products`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-gray-900 p-6 text-left align-middle shadow-xl transition-all border border-gray-700">
                <DialogTitle as="h3" className="text-lg font-medium leading-6 text-white flex justify-between items-center mb-6">
                  Internal Ads API Integration
                  <button onClick={onClose} className="text-gray-400 hover:text-white">
                    <FaTimes />
                  </button>
                </DialogTitle>

                <div className="space-y-6">
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="text-md font-medium text-blue-300 mb-2">📱 Internal Ads Integration</h4>
                    <p className="text-sm text-gray-300">Display your own products as ads within your applications using this API endpoint.</p>
                  </div>

                  {/* API Endpoint */}
                  <div>
                    <h4 className="text-md font-medium text-white mb-3">API Endpoint</h4>
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-green-400">GET</span>
                          <button
                            onClick={() => onCopyApiKey(`${window.location.origin}/api/internal-ads/serve/${placement?.apiKey}?productId=YOUR_PRODUCT_ID&lang=en`, 'API Endpoint')}
                            className="text-gray-400 hover:text-white"
                          >
                          <FaCopy />
                        </button>
                      </div>
                      <code className="text-sm text-gray-300 break-all block">
                        {getBaseUrl()}/api/internal-ads/serve/YOUR_PLACEMENT_KEY?productId=YOUR_PRODUCT_ID&lang=en
                      </code>
                    </div>
                  </div>

                  {/* Placement Key */}
                  <div>
                    <h4 className="text-md font-medium text-white mb-3">Your Placement Key (use in API)</h4>
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      {/* Table Header */}
                      <div className="grid grid-cols-12 gap-2 mb-2 px-3 py-2 bg-gray-700 rounded-lg text-xs font-medium text-gray-400">
                        <div className="col-span-1">Type</div>
                        <div className="col-span-7">Placement Title</div>
                        <div className="col-span-4">Placement Key</div>
                      </div>
                      
                      {/* Table Body */}
                      <div className="space-y-1">
                        <div className="grid grid-cols-12 gap-2 items-center bg-gray-700 rounded-lg p-3">
                          {/* Type Icon */}
                          <div className="col-span-1 flex justify-center">
                            <FaAd className="w-4 h-4 text-indigo-400" />
                          </div>
                          
                          {/* Placement Title */}
                          <div className="col-span-7">
                            <p className="text-sm text-white font-medium">{placement?.name}</p>
                          </div>
                          
                          {/* Copy Button */}
                          <div className="col-span-4">
                            <button
                              onClick={() => onCopyApiKey(placement?.apiKey, 'Placement Key')}
                              className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 transition-colors cursor-pointer"
                              title="Click to copy Placement Key"
                            >
                              {placement?.apiKey}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* Available Products */}
                  {placement?.selectedProjects && placement.selectedProjects.length > 0 ? (
                    <div>
                      <h4 className="text-md font-medium text-white mb-3">Available Products (use productId in API)</h4>
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        {/* Table Header */}
                        <div className="grid grid-cols-12 gap-2 mb-2 px-3 py-2 bg-gray-700 rounded-lg text-xs font-medium text-gray-400">
                          <div className="col-span-1">Platform</div>
                          <div className="col-span-7">Product Name</div>
                          <div className="col-span-4">Product ID</div>
                        </div>
                        
                        {/* Table Body */}
                        <div className="space-y-1">
                          {loading ? (
                            <div className="text-center py-4">
                              <p className="text-sm text-gray-400">Loading products...</p>
                            </div>
                          ) : products.length > 0 ? (
                            products.map((product) => (
                            <div key={product._id} className="grid grid-cols-12 gap-2 items-center bg-gray-700 rounded-lg p-3">
                              {/* Platform Icon */}
                              <div className="col-span-1 flex justify-center">
                                {product.platform === 'ios' ? (
                                  <FaApple className="w-4 h-4 text-gray-300" />
                                ) : product.platform === 'android' ? (
                                  <FaAndroid className="w-4 h-4 text-green-400" />
                                ) : (
                                  <FaGlobe className="w-4 h-4 text-blue-400" />
                                )}
                              </div>
                              
                              {/* Product Name */}
                              <div className="col-span-7">
                                <p className="text-sm text-white font-medium">{product.name}</p>
                              </div>
                              
                               {/* Product ID */}
                               <div className="col-span-4">
                                 <button
                                   onClick={() => onCopyApiKey(product._id, 'Product ID')}
                                   className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 transition-colors cursor-pointer"
                                   title="Click to copy Product ID"
                                 >
                                   {product._id}
                                 </button>
                               </div>
                            </div>
                            ))
                          ) : (
                            <div className="text-center py-4">
                              <p className="text-sm text-gray-400">No products found in selected projects.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h4 className="text-md font-medium text-white mb-3">Available Products (use productId in API)</h4>
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        <p className="text-sm text-gray-400 text-center py-4">
                          No products found for this project. Please add products to your project first.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Example Usage */}
                  <div>
                    <h4 className="text-md font-medium text-white mb-3">Example Usage</h4>
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <p className="text-sm text-gray-300 mb-2">HTTP Method: <span className="text-green-400">GET</span></p>
                      <p className="text-sm text-gray-300 mb-2">URL: <span className="text-blue-400">{getBaseUrl()}/api/internal-ads/serve/{placement?.apiKey}?productId=YOUR_PRODUCT_ID&lang=en</span></p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400">If <span className="font-mono">lang</span> is not provided, default language will be <span className="font-mono">en</span>.</p>

                  {/* Response Format */}
                  <div>
                    <h4 className="text-md font-medium text-white mb-3">Response Format</h4>
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <pre className="text-sm text-gray-300 bg-gray-900 p-3 rounded border overflow-x-auto">
{`{
  "success": true,
  "ad": {
    "title": "Project name from your selected projects",
    "description": "Project description from your selected projects",
    "logo": "Project logo URL (full URL)",
    "link": "Click tracking URL (redirects to actual product link)"
  }
}`}
                      </pre>
                    </div>
                  </div>

                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default InternalAdsHowToModal;
